import { Modal } from '@fl/cmsch-fe-library';
import React, { FC, memo, useCallback } from 'react';
import { Opt, opt } from 'ts-opt';
import { AnimalList } from 'api/gen/AnimalList';
import { CreateAnimalListForm } from 'app/modals/components/modal-list-name/form';
import { useOurTranslation } from 'app/translations';
import { Ant } from 'common/ant';
import { ExportAnimalsForm } from '../export-animals-form';
import { ExportAnimalsFormValues } from '../export-animals-form/export-animals-form-values';
interface Props {
  /** Message shown when no animals are available. */
  animalListEmptyMessage: string;
  /** Saved animal lists available to the user. */
  animalLists: Opt<Array<AnimalList>>;
  /** Whether the current animals page is empty. */
  isAnimalsPageEmpty: boolean;
  /** Modal title. */
  title: string;
  /** Whether the modal is visible. */
  visible: boolean;
  /** Handles export-to-file submission. */
  onSubmitSaveToFile(fileName: string): void;
  /** Handles cancel and close. */
  onCancel(): void;
  /** Handles save-to-db submission. */
  onSubmitSaveToDb(name: string): void;
}
const ModalMultipleSaveListBase: FC<Props> = ({
  animalListEmptyMessage,
  animalLists,
  isAnimalsPageEmpty,
  visible,
  title,
  onCancel,
  onSubmitSaveToDb,
  onSubmitSaveToFile
}) => {
  const {
    t
  } = useOurTranslation('animalListModals');
  const handleSubmitToFile = useCallback((values: ExportAnimalsFormValues) => opt(values.exportAnimalsInList).onSome(onSubmitSaveToFile), [onSubmitSaveToFile]);
  return <Modal onCancel={onCancel} footer={null} title={title} visible={visible} data-sentry-element="Modal" data-sentry-component="ModalMultipleSaveListBase" data-sentry-source-file="index.tsx">
            <Ant.Tabs type="card" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Ant.Tabs.TabPane key="saveToDb" tab={t('saveToDb')} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <CreateAnimalListForm isAnimalsListEmpty={isAnimalsPageEmpty} animalListEmptyMessage={animalListEmptyMessage} animalLists={animalLists} onCancel={onCancel} onSubmit={onSubmitSaveToDb} data-sentry-element="CreateAnimalListForm" data-sentry-source-file="index.tsx" />
                </Ant.Tabs.TabPane>
                <Ant.Tabs.TabPane key="saveToFile" tab={t('saveToFile')} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <ExportAnimalsForm isAnimalsPageEmpty={isAnimalsPageEmpty} onCancel={onCancel} onSubmit={handleSubmitToFile} data-sentry-element="ExportAnimalsForm" data-sentry-source-file="index.tsx" />
                </Ant.Tabs.TabPane>
            </Ant.Tabs>
        </Modal>;
};

/**
 * Memoized modal that lets the user save a list to the DB or to a file.
 *
 * @param props Shared save-list modal content and handlers.
 * @returns The multi-save list modal.
 *
 * @example
 * <ModalMultipleSaveList
 *   visible={visible}
 *   title="Save bulls"
 *   isAnimalsPageEmpty={false}
 *   animalListEmptyMessage={t('atLeastOneAnimal')}
 *   animalLists={animalLists}
 *   onSubmitSaveToDb={handleSaveToDb}
 *   onSubmitSaveToFile={handleSaveToFile}
 *   onCancel={handleCancel}
 * />
 */
export const ModalMultipleSaveList = memo(ModalMultipleSaveListBase);